import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Checkbox,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { useState } from 'react';

const MultiSelectDropdown = ({
  options = [],
  placeholder = 'Select options',
  width = '200px',
  selectedValues = [],
  onChange,
  disabled = false,
  maxDisplayItems = 2,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelectionChange = (optionValue, isChecked) => {
    let newSelectedValues;

    if (isChecked) {
      // Add to selection
      newSelectedValues = [...selectedValues, optionValue];
    } else {
      // Remove from selection
      newSelectedValues = selectedValues.filter(
        (value) => value !== optionValue,
      );
    }

    if (typeof onChange === 'function') {
      onChange(newSelectedValues);
    }
  };

  const handleRemoveTag = (valueToRemove) => {
    const newSelectedValues = selectedValues.filter(
      (value) => value !== valueToRemove,
    );
    if (typeof onChange === 'function') {
      onChange(newSelectedValues);
    }
  };

  const getDisplayContent = () => {
    if (selectedValues.length === 0) {
      return (
        <Text fontSize="sm" color="gray.500">
          {placeholder}
        </Text>
      );
    }

    if (selectedValues.length <= maxDisplayItems) {
      return (
        <Wrap spacing={1} align="center">
          {selectedValues.map((value) => {
            const option = options.find((opt) => opt.value === value);
            return (
              <WrapItem key={value}>
                <Tag
                  size="sm"
                  variant="solid"
                  bg="#EF6506"
                  color="white"
                  borderRadius="4px"
                >
                  <TagLabel fontSize="xs">{option?.label || value}</TagLabel>
                  <TagCloseButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTag(value);
                    }}
                  />
                </Tag>
              </WrapItem>
            );
          })}
        </Wrap>
      );
    }

    // Show count when too many items are selected
    return (
      <Flex align="center" gap={2}>
        <Tag
          size="sm"
          variant="solid"
          bg="#EF6506"
          color="white"
          borderRadius="4px"
        >
          <TagLabel fontSize="xs">{selectedValues.length} selected</TagLabel>
        </Tag>
        {/* <Text fontSize="sm" color="gray.600">
          {selectedValues.length > maxDisplayItems
            ? `+${selectedValues.length - maxDisplayItems} more`
            : ''}
        </Text> */}
      </Flex>
    );
  };

  const isOptionSelected = (optionValue) => {
    return selectedValues.includes(optionValue);
  };

  return (
    <Box>
      {label && (
        <Text fontSize="sm" fontWeight="500" mb={1} color="#222222">
          {label}
        </Text>
      )}
      <Popover
        placement="bottom-start"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <PopoverTrigger>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            width={width}
            minH="32px"
            border="1px solid #E7E7E7"
            borderRadius="8px"
            px={3}
            py={1}
            cursor={disabled ? 'not-allowed' : 'pointer'}
            _hover={!disabled && { borderColor: '#EF6506' }}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            opacity={disabled ? 0.5 : 1}
            bg="white"
          >
            <Box flex={1} minW={0}>
              {getDisplayContent()}
            </Box>
            <ChevronDownIcon
              ml={2}
              color={disabled ? 'gray.400' : 'gray.600'}
              transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
              transition="transform 0.2s"
            />
          </Flex>
        </PopoverTrigger>
        <PopoverContent
          width={width}
          boxShadow="lg"
          border="1px solid #EF6506"
          borderRadius="8px"
          _focus={{ outline: 'none', boxShadow: 'lg' }}
          zIndex={1000}
        >
          <PopoverBody p={0}>
            <VStack align="stretch" spacing={0} maxH="300px" overflowY="auto">
              {options.length === 0 ? (
                <Box px={3} py={3}>
                  <Text fontSize="sm" color="gray.500">
                    No options available
                  </Text>
                </Box>
              ) : (
                options.map((option) => (
                  <Box
                    key={option.value}
                    px={3}
                    py={2}
                    _hover={{ bg: '#FFF8F3' }}
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectionChange(
                        option.value,
                        !isOptionSelected(option.value),
                      );
                    }}
                    borderRadius="4px"
                    mx={1}
                    my={0.5}
                  >
                    <Flex alignItems="center" gap={2}>
                      <Checkbox
                        isChecked={isOptionSelected(option.value)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectionChange(option.value, e.target.checked);
                        }}
                        colorScheme="orange"
                        size="sm"
                      />
                      <Text fontSize="sm" flex={1}>
                        {option.label}
                      </Text>
                    </Flex>
                  </Box>
                ))
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default MultiSelectDropdown;
